/**
 * Utility to suppress React error overlay in development mode
 * This should be called early in your application bootstrap
 */

/**
 * Utility to suppress React error overlay in development mode
 * This should be called early in your application bootstrap
 */

export const suppressReactErrorOverlay = () => {
    if (process.env.NODE_ENV === 'development' || !process.env.NODE_ENV) {
        // Method 0: Set environment variables to disable overlay completely
        if (typeof window !== 'undefined') {
            window.process = window.process || {};
            window.process.env = window.process.env || {};
            window.process.env.REACT_ERROR_OVERLAY = 'false';
        }

        // Method 1: Disable React Error Overlay Global Hook immediately
        if (typeof window !== 'undefined') {
            window.__REACT_ERROR_OVERLAY_GLOBAL_HOOK__ = {
                isFiberMounted: () => false,
                onCommitFiberRoot: () => { },
                onCommitFiberUnmount: () => { },
            };
        }

        // Method 2: Override webpack's hot module replacement error handling
        if (typeof module !== 'undefined' && module?.hot) {
            module?.hot?.addErrorHandler?.((error) => {
                console.error('HMR Error caught and suppressed:', error);
                return true; // Suppress the error
            });
        }

        // Method 3: More aggressive overlay hiding
        const hideOverlayAggressive = () => {
            try {
                const overlaySelectors = [
                    'iframe[src*="react-error-overlay"]',
                    'iframe[src*="error-overlay"]',
                    '[data-react-error-overlay]',
                    'div[style*="position: fixed"][style*="z-index: 999999"]',
                    'div[style*="position: fixed"][style*="z-index: 2147483647"]',
                    'div[data-reactroot] > div[style*="position: fixed"]',
                    'div[id*="error-overlay"]',
                    'div[class*="error-overlay"]',
                    // More specific React error overlay selectors
                    'div[style*="position:fixed"][style*="top:0"][style*="left:0"][style*="width:100%"][style*="height:100%"]'
                ];

                overlaySelectors.forEach(selector => {
                    const elements = document.querySelectorAll(selector);
                    elements.forEach(element => {
                        if (element) {
                            const content = element.innerHTML || element.textContent || element.src || '';
                            if (content.toLowerCase().includes('error') ||
                                content.includes('The above error occurred') ||
                                content.includes('React will try to recreate') ||
                                element.src?.includes('error-overlay')) {
                                element.style.display = 'none !important';
                                element.style.visibility = 'hidden !important';
                                element.style.opacity = '0 !important';
                                element.remove();
                            }
                        }
                    });
                });

                // Remove any full-screen overlay divs that might be error overlays
                const fixedDivs = document.querySelectorAll('div[style*="position: fixed"]');
                fixedDivs.forEach(div => {
                    const styles = div.style;
                    if (styles.position === 'fixed' &&
                        styles.top === '0px' &&
                        styles.left === '0px' &&
                        (styles.width === '100%' || styles.right === '0px') &&
                        (styles.height === '100%' || styles.bottom === '0px') &&
                        (styles.zIndex === '999999' || styles.zIndex === '2147483647')) {
                        const content = div.innerHTML || div.textContent || '';
                        if (content.includes('Error') || content.includes('error')) {
                            div.style.display = 'none !important';
                            div.remove();
                        }
                    }
                });
            } catch (error) {
                console.warn('Error while attempting to hide React error overlay:', error);
            }
        };

        // Run overlay hiding immediately and periodically
        hideOverlayAggressive();
        setInterval(hideOverlayAggressive, 100); // More frequent checks

        // Method 4: Enhanced console.error override
        const originalConsoleError = console.error;
        console.error = (...args) => {
            const message = args[0];

            // Suppress specific React error messages more aggressively
            if (typeof message === 'string') {
                const suppressPatterns = [
                    'The above error occurred in the',
                    'React will try to recreate',
                    'Consider adding an error boundary',
                    'Warning: React.createElement',
                    'Warning: Failed prop type',
                    'Uncaught Error:',
                    'Error: ChunkLoadError',
                    'Loading chunk',
                    'Loading CSS chunk'
                ];

                if (suppressPatterns.some(pattern => message.includes(pattern))) {
                    return; // Don't log these messages
                }
            }

            originalConsoleError.apply(console, args);
        };

        // Method 5: Override window.onerror to prevent error reporting
        const originalOnError = window.onerror;
        window.onerror = (message, source, lineno, colno, error) => {
            // Suppress React-related errors
            if (typeof message === 'string' &&
                (message.includes('ChunkLoadError') ||
                    message.includes('Loading chunk') ||
                    message.includes('React') ||
                    message.includes('The above error occurred'))) {
                return true; // Prevent default error handling
            }

            // Call original handler for other errors
            if (originalOnError) {
                return originalOnError(message, source, lineno, colno, error);
            }
            return false;
        };

        // Method 6: Override unhandled rejection to suppress React-related rejections
        const originalOnUnhandledRejection = window.onunhandledrejection;
        window.onunhandledrejection = (event) => {
            const reason = event.reason;
            if (reason && typeof reason === 'object' &&
                (reason?.name === 'ChunkLoadError' ||
                    reason?.message?.includes('Loading chunk'))) {
                event.preventDefault();
                return true;
            }

            if (originalOnUnhandledRejection) {
                return originalOnUnhandledRejection(event);
            }
            return false;
        };
    }
};

export const restoreReactErrorOverlay = () => {
    if (process.env.NODE_ENV === 'development') {
        // Restore original console.error if needed
        // This would require storing the original function reference
        delete window.__REACT_ERROR_OVERLAY_GLOBAL_HOOK__;
    }
};
